import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import PageTurner from 'src/components/PageTurner'
import CloseButton from './CloseButton'
import useSwipe from 'src/hooks/useSwipe'
import Button from './Button'
import { cn } from 'src/utils'
import { ArrowLeft, ArrowRight } from 'lucide-react'

const Tutorial = ({
  tutorialId,
  numPages,
  descriptions = [],
  startPage = 1,
}: {
  tutorialId: string
  numPages: number
  descriptions?: (string | undefined)[]
  startPage?: number
}) => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [pageNum, setPageNum] = useState(startPage)
  const [acknowledged, setAcknowledged] = useState(false)

  const returnTo = searchParams.get('returnTo')

  const pageDescriptions = useMemo(
    () => descriptions?.[pageNum - 1]?.split('\n'),
    [pageNum, descriptions],
  )

  useEffect(() => {
    if (!pageDescriptions) {
      setAcknowledged(true)
      return
    }
    setAcknowledged(false)
  }, [pageNum, pageDescriptions, setAcknowledged])

  const onPageNumChange = useCallback(
    (delta: number) => {
      setPageNum((pn: number) =>
        Math.max(1, Math.min(pn + delta, numPages)),
      )
    },
    [numPages],
  )

  const direction = useSwipe()

  // Turn page on swipe
  useEffect(() => {
    if (direction && Math.abs(direction) > 80) {
      onPageNumChange(Math.sign(direction))
    }
  }, [direction, onPageNumChange, pageNum])

  return (
    <>
      <div className="display-none sm:display-block absolute left-1/4 top-1/2 -ml-8 transition ease-in-out hover:-translate-x-2 sm:z-50 lg:left-[25%] lg:top-[calc(50%-3rem)] lg:cursor-pointer lg:p-12">
        <PageTurner onClick={() => onPageNumChange(-1)}>
          ←
        </PageTurner>
      </div>

      <div className="display-none sm:display-block absolute right-1/4 top-1/2 -mr-8 transition ease-in-out hover:translate-x-2 sm:z-50 lg:right-[25%] lg:top-[calc(50%-3rem)] lg:cursor-pointer lg:p-12">
        <PageTurner onClick={() => onPageNumChange(1)}>
          →
        </PageTurner>
      </div>

      <div className="absolute top-16 left-1/2 z-[1000] mt-2 flex -translate-x-1/2 items-center gap-4 overflow-hidden rounded-full bg-white py-2 px-5 shadow-md">
        <div
          className={cn(
            `display-block sm:display-none z-50 translate-y-[0.175rem] text-sm transition ease-in-out hover:-translate-x-2 sm:z-0`,
            pageNum === 1 &&
              'pointer-events-none opacity-50',
          )}
        >
          <PageTurner onClick={() => onPageNumChange(-1)}>
            <ArrowLeft />
          </PageTurner>
        </div>

        <CloseButton
          onClick={() =>
            navigate(
              returnTo
                ? decodeURIComponent(returnTo)
                : '/',
            )
          }
        >
          Close Tutorial
        </CloseButton>

        <div
          className={cn(
            `display-block sm:display-none z-50 translate-y-[0.175rem] text-sm transition ease-in-out hover:translate-x-2 sm:z-0`,
            pageNum === numPages &&
              'pointer-events-none opacity-50',
          )}
        >
          <PageTurner onClick={() => onPageNumChange(1)}>
            <ArrowRight />
          </PageTurner>
        </div>
      </div>

      <main className="relative max-h-full w-full max-w-full flex-grow  rounded-xl sm:max-w-sm">
        <div className="relative mx-auto mt-24 flex h-full max-h-[70vh] w-full max-w-xs  flex-col justify-between overflow-hidden rounded-[3rem] border-8 border-black bg-white pt-8">
          <div className="absolute left-1/2 top-0 z-[100] h-6 w-1/3 -translate-x-1/2 rounded-b-xl bg-black">
            <div className="absolute right-4 bottom-2 h-3 w-3  rounded-full bg-white" />
          </div>

          <img
            className="pointer-events-none absolute top-0 left-0 h-full w-full select-none object-contain px-4 blur-[2px]"
            src={`/resources/tutorials/${tutorialId}/${pageNum}.png`}
            alt=""
            style={{
              ...(acknowledged ? { filter: 'none' } : {}),
            }}
          />
          {pageDescriptions && (
            <div
              className="absolute bottom-0 flex h-full w-full items-center justify-center  transition-all ease-in-out"
              style={{
                zIndex: acknowledged ? 0 : 100,
                opacity: acknowledged ? 0 : 1,
              }}
            >
              <div className="w-full">
                <p className="mx-2 select-none rounded-sm bg-white px-4 pt-4 text-sm shadow-xl">
                  {pageDescriptions.map((d, index) => (
                    <span key={index}>
                      {d}
                      <br />
                      <br />
                    </span>
                  ))}
                </p>
                <Button
                  className="bg-error relative my-2 ml-auto mr-2 w-fit bg-[rgb(143,231,138)] px-4 text-black shadow-md"
                  onClick={() => setAcknowledged(true)}
                >
                  Show Me
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  )
}

export default Tutorial
